
/**
 * work_schedule.js
* ------------------
 * Displays the list of Design Mix records in a table format.
 * Features:
 *  - Fetch Design Mix data from API
 *  - Edit & Delete actions
 *  - Export data as CSV and PDF
 *  - Uses ReactTableWrapper for table rendering
 */
import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import ListFormPageHeader from "../../components/ListFormPageHeader";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { getAllDesignMix, deleteDesignMix, printDesignMix } from "../../services/DesignMixServices";
import { openPdfResponse } from '../../utils/openPdfInApp';
import { getListViewSettings } from "../../services/SettingsServices";
import { displayError } from "../../helpers";
import { buildDesignMixQueryParams, DEFAULT_LIST_VIEW_CONFIG } from "../../utils/listViewDateRanges";
import ListPageGradientSummary from "../../components/ListPageGradientSummary";
import { buildGradientCard, formatRecordStatus, splitRecordsByStatus, sumField, formatQty } from "../../utils/listSummaryUtils";
import moment from 'moment';

function DesignMixTable() {
    const InitialData = {
					   
        records: []
      };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate(); 
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(false); 

    const right_pinned = ["id"];

    const todayDateLabel = moment().format('DD-MM-YYYY');

    const { active: activeRecords, cancelled: cancelledRecords } = useMemo(
        () => splitRecordsByStatus(data.records),
        [data.records],
    );

    const highlightCard = useMemo(() => {
        const todayCount = data.records.filter((row) => row.work_schedule_date === todayDateLabel).length;
        return buildGradientCard('Today Mix', todayCount, 'today');
    }, [data.records, todayDateLabel]);

    const summaryCards = useMemo(() => {
        const cards = [
            buildGradientCard('Total Mix', data.records.length, 'blue'),
            buildGradientCard('Sch Qty', formatQty(sumField(activeRecords, 'work_schedule_qty')), 'purple'),
            buildGradientCard('Order Qty', formatQty(sumField(activeRecords, 'ordered_quantity')), 'teal'),
        ];
        const cancelledQty = sumField(cancelledRecords, 'work_schedule_qty');
        if (cancelledRecords.length && cancelledQty > 0) {
            cards.push(buildGradientCard('Cancelled Sch. Qty', formatQty(cancelledQty), 'red'));
        }
        return cards;
    }, [data.records.length, activeRecords, cancelledRecords]);

    const columns = useMemo(
        () => [
              {
                fieldName: "id",
                headerName: "ROW_ACTIONS"
            },

            {
                fieldName: 'plant', 
                headerName: 'Plant'
            },
                        {
                fieldName: "customer",
                headerName: 'Customer'
            },
            {
                fieldName: "site",
                headerName: 'Site'
            },
            {
                fieldName: "grade",
                headerName: 'Grade'
            },
            
            {
                fieldName: "work_schedule_qty",
                headerName: 'Sch. Qty.'
            },
            {
                fieldName: "delivery_mode",
                headerName: 'Del. Mode'
            },
            {
                fieldName: "status",
                headerName: 'Status'
            },
            {
                fieldName: "conc_structure",
                headerName: 'Con Structure'
            },
            //     :r.water2,
            //     other_3:r.adm1,
            //     other_4:r.adm2,
            //     other_5:r.others
            {
                fieldName: 'silo_1', 
                headerName: 'Silo 1'
            },
            {
                fieldName: 'silo_2', 
                headerName: 'Silo 2'
            },
            {
                fieldName: "silo_3",
                headerName: 'Silo 3'
            },
            {
                fieldName: 'silo_4', 
                headerName: 'Silo 4'
            },
            {
                fieldName: "silo_5",
                headerName: 'Silo 5'
            },
            {
                fieldName: 'bin_1', 
                headerName: 'Bin 1'
            },
            {
                fieldName: 'bin_2', 
                headerName: 'Bin 2'
            },
            {
                fieldName: "bin_3",
                headerName: 'Bin 3'
            },
            {
                fieldName: 'bin_4', 
                headerName: 'Bin 4'
            },
            {
                fieldName: "bin_5",
                headerName: 'Bin 5'
            },
            {
                fieldName: 'other_1', 
                headerName: 'Other 1'
            },
            {
                fieldName: 'other_2', 
                headerName: 'Other 2'
            },
            {
                fieldName: "other_3",
                headerName: 'Other 3'
            },
            {
                fieldName: 'other_4', 
                headerName: 'Other 4'
            },
            {
                fieldName: "other_5",
                headerName: 'Other 5'
            },
          {
                fieldName: 'sales_order_no', 
                headerName: 'SO No.'
            },
            {
                fieldName: "sales_order_date",
                headerName: 'SO Date'
            },
            {
                fieldName: 'work_schedule_no', 
                headerName: 'WS No'
            },
            {
                fieldName: "work_schedule_date",
                headerName: 'WS Date'
            }

        ],
        [],);

    const onEditDesignMix = (designmixId, event) => {
       navigate(`/DesignMixEdit/${designmixId}/`);
    };

    const onPrintDesignMix = async (designmixId, event) => {
        event?.preventDefault?.();
        try {
            const blob = await printDesignMix(cookies, designmixId);
            openPdfResponse(blob, {
                title: 'Design Mix PDF',
                downloadFileName: `DesignMix_${designmixId}.pdf`,
            });
        } catch (error) {
            let message = error?.response?.data ?? error.message;
            if (message instanceof Blob) {
                try {
                    const text = await message.text();
                    message = JSON.parse(text);
                } catch {
                    message = 'PDF generation failed on server.';
                }
            }
            displayError(message, 'Print Failed');
        }
    };

    const onDeleteDesignMix = (designmixId, event) => {
        event.preventDefault();
        Swal.fire({title: 'Are you sure to Delete?',  
        showCancelButton: true,  
        confirmButtonText: `Yes`,  
        cancelButtonText: `No`,
        }).then((result) => {  
          if (result.isConfirmed) { 
            setIsLoading(true);
            deleteDesignMix(cookies, designmixId)
            .then(() => {
                console.log(`DesignMix with id ${designmixId} deleted`);
                Swal.fire('Deleted Successfully!', 'Work schedule is eligible for a new mix.', 'success');
                setRefreshKey(oldKey => oldKey +1);
            })
            .catch((error) => {
                console.log(error.response?.data);
                displayError(error.response?.data, 'Delete Failed');
            })
            .finally(() => setIsLoading(false));
          } else if (result.isDismissed) {    
            Swal.fire('Not Deleted', '', 'info')  
        }
        });
    };

const mapDesignMixRows = (rows) => rows.map(r => {
    const isActive = Boolean(r.status);
    return {
    id: r.id,
    sales_order_no: r.sales_order_no || '',
    sales_order_date: r.sales_order_date || '',
    work_schedule_no: r.work_schedule_no || '',
    work_schedule_date: r.work_schedule_date || '',
    customer: (r.customer || '').toUpperCase(),
    site: (r.site || '').toUpperCase(),
    work_schedule_qty: r.work_schedule_qty,
    grade: r.grade || '',
    rate: r.rate,
    conc_structure: (r.conc_structure || '').toUpperCase(),
    ordered_quantity: r.work_schedule?.sales_order_detail?.quantity ?? r.work_schedule_qty,
    delivery_mode: (r.delivery_mode || '').toUpperCase(),
    plant: r.plant || '',
    status: formatRecordStatus(isActive),
    is_inactive: !isActive,
    silo_1: r.cem1,
    silo_2: r.cem2,
    silo_3: r.cem3,
    silo_4: r.cem4,
    silo_5: r.cem5,
    bin_1: r.agg1,
    bin_2: r.agg2,
    bin_3: r.agg3,
    bin_4: r.agg4,
    bin_5: r.agg5,
    other_1: r.water1,
    other_2: r.water2,
    other_3: r.adm1,
    other_4: r.adm2,
    other_5: r.others
};
});

const loadDesignMixList = useCallback((config) => {
    if (!cookies.myToken) {
        setIsLoading(false);
        return;
    }
    const activeConfig = config || DEFAULT_LIST_VIEW_CONFIG;
    setIsLoading(true);
    getAllDesignMix(cookies, buildDesignMixQueryParams(activeConfig))
        .then(designmixList => {
            setData({ records: mapDesignMixRows(designmixList?.design_mix_list || []) });
        })
        .catch((error) => {
            setData({ records: [] });
            displayError(error.response?.data ?? error.message, 'Failed to load design mix list');
        })
        .finally(() => {
            setIsLoading(false);
        });
}, [cookies]);

const handleSettingsSaved = useCallback((config) => {
    loadDesignMixList(config);
}, [loadDesignMixList]);

useEffect(() => {
    if (!cookies.myToken) {
        setIsLoading(false);
        return;
    }
    getListViewSettings(cookies, 'design_mix')
        .then((response) => {
            loadDesignMixList(response.config || DEFAULT_LIST_VIEW_CONFIG);
        })
        .catch(() => {
            loadDesignMixList(DEFAULT_LIST_VIEW_CONFIG);
        });
}, [cookies, cookies.myToken, loadDesignMixList, refreshKey]);

    const handleCSVExport = (event) => {
        console.log('CSV Export');
        exportAsCSV(
            columns
            .filter(col => col.headerName !== 'ROW_ACTIONS')
            .map(c => c.headerName),
            data.records.map(r => {

                const csvRow = {
                    plant:r.plant,
                    sales_order_no: r.sales_order_no,
                    sales_order_date: r.sales_order_date,
                    customer_id:r.customer_id,
                    consignee_id: r.consignee_id,
                    grade: r.grade,
                    ordered_quantity: r.ordered_quantity,
                    delivery_mode:r.delivery_mode,
                    conc_structure: r.conc_structure,
                    work_schedule_no :r.work_schedule_no,
                    work_schedule_date:r.work_schedule_date,
                    scheduled_quantity:r.scheduled_quantity
                }
                return csvRow;
                }));
    };

    const handlePDFExport = (event) => {
        console.log('PDF Export');
        exportAsPDF(
            'List of Design Mix Form',
            columns
            .filter(col => col.headerName !== 'ROW_ACTIONS')
            .map(col=>({header: col.headerName, dataKey: col.fieldName })),
            data.records.map(r => ({
                plant:r.plant,
                sales_order_no: r.sales_order_no,
                sales_order_date: r.sales_order_date,
                customer_id:r.customer_id,
                consignee_id: r.consignee_id,
                grade: r.grade,
                rate:r.rate,
                ordered_quantity: r.ordered_quantity,
                delivery_mode:r.delivery_mode,
                conc_structure: r.conc_structure,
                work_schedule_no :r.work_schedule_no,
                work_schedule_date:r.work_schedule_date,
                scheduled_quantity:r.scheduled_quantity
            })),
            'DesignMixList.pdf'
        );
    };

        return (

        <div id="outer-container"  className="App" > 
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <div id="page-wrap">
                <ListFormPageHeader
                    module="QUALITY CONTROL"
                    title="List of Design Mix"
                    viewKey="design_mix"
                    onSettingsSaved={handleSettingsSaved}
                    formLink="/DesignMix"
                    addNewLabel="New Design Mix"
                />

                <ListPageGradientSummary highlightCard={highlightCard} cards={summaryCards} />

                <div className="container item-list-table-container">
                <div className="table-responsive">
                <ReactTableWrapper
                            title='List of Group Form'
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditDesignMix}
                            onRowPrint={onPrintDesignMix}
                            onRowDelete={onDeleteDesignMix}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            hideDelete={true}
                            hidePrint={false}
                            right_pinned={right_pinned}
                        />
                </div>
                </div> 


            </div>
        </div>

        
    );

}



export default DesignMixTable;